/**
 * Variant Picker — standalone JS (Section Rendering API architecture)
 *
 * Supports 2048+ variants by NOT embedding all variants in JSON.
 * Instead, on each option change, calls Section Rendering API with option_value IDs.
 * The server re-renders the section with correct availability, prices, and variant data.
 *
 * Features:
 * - Option selection (click on block/swatch)
 * - Section Rendering API fetch on change → server resolves variant
 * - DOM replacement of picker + price + stock areas
 * - Dispatches variant:change event for gallery + price updates
 * - Stock limit check before add-to-cart
 * - URL update with selected variant
 */
(function () {
  'use strict';

  // ========================================================================
  // TOAST
  // ========================================================================

  function showToast(message, isError) {
    if (!document.querySelector('.quick-add-toast-styles')) {
      var style = document.createElement('style');
      style.className = 'quick-add-toast-styles';
      style.textContent = '.quick-add-toast{position:fixed;bottom:24px;left:50%;transform:translateX(-50%) translateY(20px);z-index:2147483647;background:#31af54;color:#fff;font-size:14px;font-weight:600;padding:12px 24px;border-radius:999px;box-shadow:0 4px 20px rgba(0,0,0,.12);opacity:0;animation:quickAddToastIn .3s ease forwards,quickAddToastOut .3s ease 2.2s forwards;pointer-events:none}.quick-add-toast--error{background:#dc2626}@keyframes quickAddToastIn{to{opacity:1;transform:translateX(-50%) translateY(0)}}@keyframes quickAddToastOut{to{opacity:0;transform:translateX(-50%) translateY(10px)}}';
      document.head.appendChild(style);
    }
    var toast = document.createElement('div');
    toast.className = 'quick-add-toast' + (isError ? ' quick-add-toast--error' : '');
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(function () {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, 2600);
  }

  // ========================================================================
  // CART HELPERS
  // ========================================================================

  function getCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (res) { return res.json(); });
  }

  function getCartQuantityForVariant(cart, variantId) {
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === variantId) {
        return cart.items[i].quantity;
      }
    }
    return 0;
  }

  function getCartQuantityForProduct(cart, productId) {
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].product_id === productId) {
        total += cart.items[i].quantity;
      }
    }
    return total;
  }

  // ========================================================================
  // INIT
  // ========================================================================

  var pickers = document.querySelectorAll('.variant-picker');
  if (!pickers.length) return;

  pickers.forEach(initPicker);

  function initPicker(picker) {
    var sectionId = picker.getAttribute('section-id');
    var formId = picker.getAttribute('form-id');
    var updateUrl = picker.hasAttribute('update-url');
    var productUrl = picker.getAttribute('data-product-url') || window.location.pathname;

    // Parse current variant from embedded JSON
    var currentVariant = null;
    var variantScript = picker.querySelector('script[data-variant]');
    if (variantScript) {
      try { currentVariant = JSON.parse(variantScript.textContent); } catch (e) { /* null */ }
    }

    // Debounce tracker
    var fetchController = null;
    var isFetching = false;
    var fetchGeneration = 0;

    // ========================================================================
    // VARIANT DATA (pre-fetch correction)
    // ========================================================================

    var optionMapping = {};
    var reverseMapping = {};
    var variantCombos = [];

    function parseVariantData() {
      optionMapping = {};
      reverseMapping = {};
      variantCombos = [];
      var mappingScript = picker.querySelector('script[data-option-mapping]');
      if (mappingScript) {
        try { optionMapping = JSON.parse(mappingScript.textContent); } catch (e) {}
      }
      for (var pos in optionMapping) {
        if (!optionMapping.hasOwnProperty(pos)) continue;
        reverseMapping[pos] = {};
        var map = optionMapping[pos];
        for (var name in map) {
          if (!map.hasOwnProperty(name)) continue;
          reverseMapping[pos][map[name]] = name;
        }
      }
      var combosScript = picker.querySelector('script[data-variant-combos]');
      if (combosScript) {
        try { variantCombos = JSON.parse(combosScript.textContent); } catch (e) {}
      }
    }
    parseVariantData();

    function correctOptionValueIds(optionValueIds, changedPosition) {
      if (!variantCombos.length || !Object.keys(reverseMapping).length) return null;
      var optionNames = optionValueIds.map(function(id, i) {
        var rMap = reverseMapping[String(i + 1)];
        return rMap ? (rMap[id] || id) : id;
      });
      var exists = variantCombos.some(function(v) {
        return v.o.every(function(opt, i) { return opt === optionNames[i]; });
      });
      if (exists) return null;
      var changedIndex = parseInt(changedPosition, 10) - 1;
      var changedName = optionNames[changedIndex];
      // 1) First available variant with the changed option
      var best = null;
      for (var i = 0; i < variantCombos.length; i++) {
        if (variantCombos[i].o[changedIndex] === changedName && variantCombos[i].a) {
          best = variantCombos[i]; break;
        }
      }
      // 2) Any variant with the changed option (even OOS)
      if (!best) {
        for (var i = 0; i < variantCombos.length; i++) {
          if (variantCombos[i].o[changedIndex] === changedName) {
            best = variantCombos[i]; break;
          }
        }
      }
      if (!best) return null;
      var corrected = best.o.map(function(name, i) {
        var fMap = optionMapping[String(i + 1)];
        return fMap ? fMap[name] : null;
      });
      if (corrected.some(function(id) { return !id; })) return null;
      return corrected;
    }

    // ========================================================================
    // OPTION CHANGE HANDLER
    // ========================================================================

    bindOptionSelectors();

    function bindOptionSelectors() {
      var optionSelectors = picker.querySelectorAll('[data-option-selector]');
      optionSelectors.forEach(function (selector) {
        selector.addEventListener('change', onOptionChange);
      });
    }

    function onOptionChange(event) {
      var optionValueIds = getSelectedOptionValueIds();
      if (!optionValueIds.length) return;

      // Capture which option the user clicked (before DOM replacement)
      var changedInput = event.target;
      var changedPosition = changedInput ? changedInput.getAttribute('data-option-position') : null;

      // Pre-fetch correction: fix invalid combo BEFORE the fetch
      if (changedPosition) {
        var corrected = correctOptionValueIds(optionValueIds, changedPosition);
        if (corrected) optionValueIds = corrected;
      }

      // Set loading state
      picker.classList.add('is-loading');

      // Abort any in-flight request
      if (fetchController) {
        fetchController.abort();
      }
      fetchController = new AbortController();
      var currentGeneration = ++fetchGeneration;

      var url = productUrl + '?option_values=' + optionValueIds.join(',') + '&section_id=' + sectionId;

      isFetching = true;
      fetch(url, { signal: fetchController.signal })
        .then(function (res) { return res.text(); })
        .then(function (html) {
          isFetching = false;
          fetchController = null;
          if (currentGeneration !== fetchGeneration) return;
          applyServerResponse(html);
        })
        .catch(function (err) {
          isFetching = false;
          fetchController = null;
          if (err.name !== 'AbortError') {
            picker.classList.remove('is-loading');
          }
        });
    }

    // ========================================================================
    // COLLECT SELECTED OPTION VALUE IDS
    // ========================================================================

    function getSelectedOptionValueIds() {
      var ids = [];
      var radios = picker.querySelectorAll('[data-option-selector] input[type="radio"]:checked');
      radios.forEach(function (radio) {
        var optionValueId = radio.getAttribute('data-option-value-id');
        if (optionValueId) {
          ids.push(optionValueId);
        }
      });
      return ids;
    }

    // ========================================================================
    // APPLY SERVER RESPONSE (replace DOM)
    // ========================================================================

    function applyServerResponse(html) {
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');

      // --- Replace the variant picker itself ---
      var newPicker = doc.querySelector('.variant-picker');
      if (newPicker) {
        picker.innerHTML = newPicker.innerHTML;
        parseVariantData();
        bindOptionSelectors();
      }

      // --- Extract new variant data ---
      var newVariantScript = picker.querySelector('script[data-variant]');
      var newVariant = null;
      if (newVariantScript) {
        try { newVariant = JSON.parse(newVariantScript.textContent); } catch (e) { /* null */ }
      }

      currentVariant = newVariant;
      updateFormInput(newVariant);
      if (updateUrl && newVariant) updateUrlParam(newVariant.id);
      updateElement('.product-heading__price', doc);
      updateElement('.product-heading__top-right', doc);
      updateElement('.product-heading__meta', doc);
      updateElement('.product-heading__badges', doc);
      updateElement('[data-product-payment-terms]', doc);
      updateBuyButtons(newVariant);
      picker.classList.remove('is-loading');

      var event = new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant: newVariant, sectionId: sectionId }
      });
      document.dispatchEvent(event);
    }

    // ========================================================================
    // UPDATE FORM INPUT
    // ========================================================================

    function updateFormInput(variant) {
      var form = document.getElementById(formId);
      if (!form) return;

      var input = form.querySelector('input[name="id"]');
      if (input) {
        if (variant) {
          input.value = variant.id;
          input.disabled = false;
        } else {
          input.disabled = true;
        }
      }
    }

    // ========================================================================
    // UPDATE URL
    // ========================================================================

    function updateUrlParam(variantId) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    }

    // ========================================================================
    // UPDATE ELEMENT (from parsed server response)
    // ========================================================================

    function updateElement(selector, doc) {
      var newEl = doc.querySelector(selector);
      var currentEl = document.querySelector(selector);
      if (newEl && currentEl) {
        currentEl.innerHTML = newEl.innerHTML;
      }
    }

    // ========================================================================
    // UPDATE BUY BUTTONS
    // ========================================================================

    function updateBuyButtons(variant) {
      var form = document.getElementById(formId);
      if (!form) return;

      var submitBtn = form.querySelector('button[type="submit"], [type="submit"]');
      if (!submitBtn) return;

      var strings = window.themeVariables && window.themeVariables.strings || {};

      // Sync data-main-sold-out on <buy-buttons> element
      var buyButtonsEl = form.querySelector('buy-buttons');

      // Coming-soon template: always force disabled + data-main-sold-out
      var isComingSoon = buyButtonsEl && buyButtonsEl.getAttribute('template') === 'coming-soon';

      if (isComingSoon) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Coming soon';
        var paymentBtnCS = form.closest('.buy-buttons') || form.parentElement;
        if (paymentBtnCS) {
          var shopifyPaymentCS = paymentBtnCS.querySelector('.shopify-payment-button');
          if (shopifyPaymentCS) shopifyPaymentCS.style.display = 'none';
        }
        if (buyButtonsEl) buyButtonsEl.setAttribute('data-main-sold-out', '');
      } else if (variant && variant.available) {
        submitBtn.disabled = false;
        submitBtn.removeAttribute('data-comp-only-mode');
        submitBtn.textContent = (variant.metafields && variant.metafields.custom && variant.metafields.custom['pre-order'])
          ? (strings.preOrderButton || 'Pré-commander')
          : (strings.addToCartButton || 'Ajouter au panier');
        var paymentBtn = form.closest('.buy-buttons') || form.parentElement;
        if (paymentBtn) {
          var shopifyPayment = paymentBtn.querySelector('.shopify-payment-button');
          if (shopifyPayment) shopifyPayment.style.display = '';
        }
        if (buyButtonsEl) buyButtonsEl.removeAttribute('data-main-sold-out');
      } else if (variant) {
        submitBtn.disabled = true;
        submitBtn.textContent = strings.soldOutButton || 'En rupture';
        var paymentBtn2 = form.closest('.buy-buttons') || form.parentElement;
        if (paymentBtn2) {
          var shopifyPayment2 = paymentBtn2.querySelector('.shopify-payment-button');
          if (shopifyPayment2) shopifyPayment2.style.display = 'none';
        }
        if (buyButtonsEl) buyButtonsEl.setAttribute('data-main-sold-out', '');
      } else {
        submitBtn.disabled = true;
        if (buyButtonsEl) buyButtonsEl.removeAttribute('data-main-sold-out');
      }

      // Notify complementary-only logic to re-evaluate button state
      document.dispatchEvent(new CustomEvent('variant:buybuttons-updated', { bubbles: true }));
    }

    // ========================================================================
    // FORM SUBMIT HANDLER (stock check before add-to-cart)
    // ========================================================================

    var form = document.getElementById(formId);
    if (form && form.getAttribute('action') && form.getAttribute('action').indexOf('/cart/add') !== -1) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();

        var submitBtn = form.querySelector('[type="submit"]');

        // Complementary-only mode: dispatch event so _buy-button.liquid handles it
        if (submitBtn && submitBtn.hasAttribute('data-comp-only-mode')) {
          submitBtn.disabled = true;
          document.dispatchEvent(new CustomEvent('cart:add-complementary-only', { bubbles: true }));
          return;
        }

        var variantInput = form.querySelector('input[name="id"]');
        var localFormId = form.getAttribute('id');
        var qtyInput = form.querySelector('input[name="quantity"]')
          || document.querySelector('input[name="quantity"][form="' + localFormId + '"]');
        var variantId = variantInput ? parseInt(variantInput.value, 10) : null;
        var requestedQty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        if (!variantId) return;
        if (submitBtn) submitBtn.disabled = true;

        var buyLimitRaw = form.getAttribute('data-buy-limit');
        var buyLimit = buyLimitRaw ? parseInt(buyLimitRaw, 10) : 0;

        if (!buyLimit) {
          doFormAdd(form, submitBtn, variantId, requestedQty);
          return;
        }

        var productId = currentVariant ? currentVariant.product_id : null;

        getCart().then(function (cart) {
          var inCartProduct = productId ? getCartQuantityForProduct(cart, productId) : 0;
          var maxAddable = buyLimit - inCartProduct;

          if (maxAddable <= 0) {
            if (submitBtn) submitBtn.disabled = false;
            showToast('Limite d\u2019achat atteinte (' + buyLimit + ' max)', true);
            return;
          }

          if (maxAddable < requestedQty) {
            doFormAdd(form, submitBtn, variantId, maxAddable, requestedQty);
          } else {
            doFormAdd(form, submitBtn, variantId, requestedQty);
          }
        }).catch(function () {
          doFormAdd(form, submitBtn, variantId, requestedQty);
        });
      });
    }

    function doFormAdd(form, submitBtn, variantId, qty, originalQty) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: variantId, quantity: qty })
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (data) { throw new Error(data.description || 'Erreur lors de l\u2019ajout'); });
          return res.json();
        })
        .then(function () {
          document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:main-product-added', { bubbles: true }));

          fetch('/cart.js')
            .then(function (res) { return res.json(); })
            .then(function (cart) {
              var cartCounts = document.querySelectorAll('[data-cart-count]');
              cartCounts.forEach(function (el) {
                el.textContent = cart.item_count;
                if (cart.item_count > 0) {
                  el.removeAttribute('hidden');
                }
              });
            });

          if (submitBtn) submitBtn.disabled = false;

          if (originalQty && qty < originalQty) {
            showToast('Seulement ' + qty + ' sur ' + originalQty + ' ajout\u00e9(s) \u2014 limite atteinte', true);
          } else {
            showToast('Ajout\u00e9 au panier \u2713');
          }
        })
        .catch(function (err) {
          if (submitBtn) submitBtn.disabled = false;
          showToast(err.message || 'Erreur lors de l\u2019ajout', true);
        });
    }
  }
})();
